import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Camera, 
  QrCode, 
  X, 
  Loader2, 
  CheckCircle2,
  AlertCircle,
  RotateCcw,
  Sparkles,
  Gift
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface TicketScannerProps {
  onPointsEarned?: (data: {
    pointsAdded: number;
    newBalance: number;
    orderNumber: number;
    leveledUp: boolean;
    newLevel: number;
    newPrize?: any;
  }) => void;
}

const TicketScanner: React.FC<TicketScannerProps> = ({ onPointsEarned }) => {
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [manualInput, setManualInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    data?: any;
  } | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  useEffect(() => {
    if (scanning && videoRef.current && streamRef.current) {
      const video = videoRef.current;
      video.srcObject = streamRef.current;
      video.play().catch((err) => {
        console.error('Error playing video:', err);
        setCameraError('No se pudo iniciar la cámara');
      });
    }
  }, [scanning]);

  const startCamera = async () => {
    setCameraError(null);
    setResult(null);

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Tu navegador no soporta acceso a la cámara');
      return;
    }

    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      streamRef.current = stream;
      setScanning(true);
    } catch (error: any) {
      let errorMessage = 'No se pudo acceder a la cámara';
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Permiso de cámara denegado. Actívalo en configuración.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No se encontró ninguna cámara';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'La cámara está siendo usada por otra app';
      }
      setCameraError(errorMessage);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setScanning(false);
    setCameraError(null);
  };

  const redeemTicket = async (token: string) => {
    setLoading(true);
    setResult(null);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Debes iniciar sesión para escanear tickets');
      }

      const { data, error } = await supabase.functions.invoke('redeem-ticket-points', {
        body: { ticket_token: token },
      });

      if (error) throw error;

      if (data.success) {
        stopCamera();
        setResult({
          success: true,
          message: `¡Has ganado ${data.points_added} puntos!`,
          data,
        });

        if (data.leveled_up && data.new_prize) {
          toast.success(`🎉 ¡Subiste al nivel ${data.new_level}!`, {
            description: `Premio desbloqueado: ${data.new_prize.rewards_pool?.name || 'Sorpresa'}`,
            duration: 5000,
          });
        } else {
          toast.success(`+${data.points_added} puntos añadidos`);
        }

        onPointsEarned?.({
          pointsAdded: data.points_added,
          newBalance: data.new_balance,
          orderNumber: data.order_number,
          leveledUp: data.leveled_up,
          newLevel: data.new_level,
          newPrize: data.new_prize,
        });
      } else {
        throw new Error(data.error || 'Error desconocido');
      }
    } catch (err: any) {
      const errorMsg = err.message || 'Error al procesar el ticket';
      setResult({
        success: false,
        message: errorMsg,
      });
      toast.error('Error', { description: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleManualRedeem = () => {
    if (!manualInput.trim()) return;
    
    // Parse the QR content - expected format: {"type":"ticket","token":"uuid"}
    let token = manualInput.trim();
    
    try {
      const parsed = JSON.parse(token);
      if (parsed.type === 'ticket' && parsed.token) {
        token = parsed.token;
      }
    } catch {
      // Assume raw token was entered
    }
    
    redeemTicket(token);
  };

  const resetScanner = () => {
    setResult(null);
    setManualInput('');
  };

  return (
    <Card className="max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <QrCode className="h-5 w-5 text-secondary" />
          Escanear Ticket
        </CardTitle>
        <CardDescription>
          Escanea el QR de tu factura para sumar puntos
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {result ? (
          <div className={`p-6 rounded-lg text-center ${
            result.success 
              ? 'bg-green-50 border-2 border-green-200' 
              : 'bg-red-50 border-2 border-red-200'
          }`}>
            {result.success ? (
              <>
                <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <p className="text-2xl font-bold text-green-700 mb-2">
                  +{result.data?.points_added} puntos
                </p>
                <p className="text-sm text-green-600">
                  Ticket #{result.data?.order_number} • Total: {result.data?.order_total?.toFixed(2)}€
                </p>
                <p className="text-lg font-semibold text-green-700 mt-2">
                  Nuevo saldo: {result.data?.new_balance} pts
                </p>
                
                {result.data?.leveled_up && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-amber-100 to-yellow-100 rounded-lg">
                    <Sparkles className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                    <p className="font-bold text-amber-700">
                      ¡Subiste al Nivel {result.data?.new_level}!
                    </p>
                    {result.data?.new_prize && (
                      <div className="mt-2 flex items-center justify-center gap-2 text-amber-600">
                        <Gift className="h-4 w-4" />
                        <span>Premio: {result.data.new_prize.rewards_pool?.name}</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <p className="text-lg font-semibold text-red-700 mb-2">
                  No se pudo canjear
                </p>
                <p className="text-sm text-red-600">{result.message}</p>
              </>
            )}
            
            <Button 
              onClick={resetScanner} 
              variant="outline" 
              className="mt-4"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Escanear otro ticket
            </Button>
          </div>
        ) : (
          <>
            {/* Camera Section */}
            <div 
              className="relative bg-muted rounded-lg overflow-hidden"
              style={{ minHeight: '280px' }}
            >
              {scanning ? (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                    style={{ minHeight: '280px' }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-48 h-48 border-2 border-secondary rounded-lg animate-pulse" />
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute top-2 right-2 z-10"
                    onClick={stopCamera}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <p className="absolute bottom-2 left-0 right-0 text-center text-white text-sm bg-black/50 py-1">
                    Apunta al QR del ticket
                  </p>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center gap-4 py-8" style={{ minHeight: '280px' }}>
                  {cameraError ? (
                    <>
                      <div className="text-destructive text-center px-4">
                        <Camera className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p className="text-sm font-medium">{cameraError}</p>
                      </div>
                      <Button onClick={startCamera} variant="outline">
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reintentar
                      </Button>
                    </>
                  ) : (
                    <>
                      <Camera className="h-12 w-12 text-muted-foreground" />
                      <Button onClick={startCamera} size="lg">
                        <Camera className="h-5 w-5 mr-2" />
                        Activar Cámara
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Manual Input */}
            <div className="space-y-2 pt-4 border-t">
              <Label htmlFor="manual-token">O introduce el código manualmente</Label>
              <div className="flex gap-2">
                <Input
                  id="manual-token"
                  placeholder="Código del ticket"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleManualRedeem()}
                />
                <Button 
                  onClick={handleManualRedeem} 
                  disabled={loading || !manualInput.trim()}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Canjear'
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TicketScanner;
